/**
 * Americano pairing algorithm:
 * - Each round creates new random pairs
 * - No player should partner with the same player twice (best effort)
 * - Distributes matches across available courts
 *
 * Mexicano pairing algorithm:
 * - Round 1: random pairs
 * - Round 2+: pair players with similar total points (top plays top)
 */

interface PlayerScore {
  userId: string;
  totalPoints: number;
}

interface TournamentMatch {
  team1: string[];
  team2: string[];
  courtIndex: number;
}

/**
 * Generate Americano round pairings.
 * Players are shuffled randomly and paired up.
 * previousPartners tracks who has partnered with whom to avoid repeats.
 */
export function generateAmericanoRound(
  playerIds: string[],
  courtCount: number,
  previousPartners: Map<string, Set<string>>
): TournamentMatch[] {
  const shuffled = shuffle([...playerIds]);
  const matches: TournamentMatch[] = [];

  // Try to create pairs avoiding previous partners
  const paired = pairPlayersAvoidingRepeats(shuffled, previousPartners);

  // Group pairs into matches (2 pairs per match = 4 players)
  for (let i = 0; i < paired.length - 1; i += 2) {
    matches.push({
      team1: paired[i],
      team2: paired[i + 1],
      courtIndex: Math.floor(i / 2) % courtCount,
    });
  }

  return matches;
}

/**
 * Generate Mexicano round pairings.
 * Round 1: random. Round 2+: sort by points, pair adjacent players.
 */
export function generateMexicanoRound(
  players: PlayerScore[],
  courtCount: number,
  roundNumber: number,
  previousPartners: Map<string, Set<string>>
): TournamentMatch[] {
  if (roundNumber === 1) {
    return generateAmericanoRound(
      players.map((p) => p.userId),
      courtCount,
      previousPartners
    );
  }

  // Sort by total points descending
  const sorted = [...players].sort((a, b) => b.totalPoints - a.totalPoints);
  const playerIds = sorted.map((p) => p.userId);

  // Group into fours: [1st,2nd,3rd,4th], [5th,6th,7th,8th], etc.
  // Within each group: 1st+4th vs 2nd+3rd (cross-pairing for balance)
  const matches: TournamentMatch[] = [];

  for (let i = 0; i < playerIds.length; i += 4) {
    const group = playerIds.slice(i, i + 4);
    if (group.length === 4) {
      matches.push({
        team1: [group[0], group[3]], // 1st + 4th
        team2: [group[1], group[2]], // 2nd + 3rd
        courtIndex: Math.floor(i / 4) % courtCount,
      });
    }
  }

  return matches;
}

/**
 * Update partner tracking map after a round.
 */
export function updatePartnerHistory(
  history: Map<string, Set<string>>,
  matches: TournamentMatch[]
): Map<string, Set<string>> {
  const updated = new Map(history);

  for (const match of matches) {
    for (const team of [match.team1, match.team2]) {
      if (team.length === 2) {
        const [a, b] = team;
        if (!updated.has(a)) updated.set(a, new Set());
        if (!updated.has(b)) updated.set(b, new Set());
        updated.get(a)!.add(b);
        updated.get(b)!.add(a);
      }
    }
  }

  return updated;
}

/**
 * Calculate updated scores after a match result.
 */
export function calculateMatchPoints(
  team1Score: number,
  team2Score: number,
  pointsPerMatch: number
): { team1Points: number; team2Points: number } {
  // In Americano/Mexicano, points are distributed based on score
  // Total points in a match = pointsPerMatch, split by score ratio
  const totalScore = team1Score + team2Score;
  if (totalScore === 0) {
    return { team1Points: pointsPerMatch / 2, team2Points: pointsPerMatch / 2 };
  }

  return {
    team1Points: team1Score,
    team2Points: team2Score,
  };
}

// --- Team Mexicano ---

interface TeamScore {
  teamId: string;
  players: string[];
  totalPoints: number;
}

interface TeamMatch {
  team1Id: string;
  team2Id: string;
  team1Players: string[];
  team2Players: string[];
  courtIndex: number;
}

export function generateTeamMexicanoRound(
  teams: TeamScore[],
  courtCount: number,
  roundNumber: number,
  previousOpponents: Map<string, Set<string>>
): TeamMatch[] {
  const matches: TeamMatch[] = [];

  if (roundNumber === 1) {
    const shuffled = shuffle([...teams]);
    for (let i = 0; i < shuffled.length - 1; i += 2) {
      matches.push({
        team1Id: shuffled[i].teamId,
        team2Id: shuffled[i + 1].teamId,
        team1Players: shuffled[i].players,
        team2Players: shuffled[i + 1].players,
        courtIndex: Math.floor(i / 2) % courtCount,
      });
    }
  } else {
    const sorted = [...teams].sort((a, b) => b.totalPoints - a.totalPoints);
    const used = new Set<string>();

    for (const team of sorted) {
      if (used.has(team.teamId)) continue;

      let bestOpponent: TeamScore | null = null;
      for (const candidate of sorted) {
        if (candidate.teamId === team.teamId || used.has(candidate.teamId)) continue;
        const prevOpp = previousOpponents.get(team.teamId);
        if (!prevOpp || !prevOpp.has(candidate.teamId)) {
          bestOpponent = candidate;
          break;
        }
      }

      if (!bestOpponent) {
        bestOpponent = sorted.find((c) => c.teamId !== team.teamId && !used.has(c.teamId)) ?? null;
      }

      if (bestOpponent) {
        matches.push({
          team1Id: team.teamId,
          team2Id: bestOpponent.teamId,
          team1Players: team.players,
          team2Players: bestOpponent.players,
          courtIndex: matches.length % courtCount,
        });
        used.add(team.teamId);
        used.add(bestOpponent.teamId);
      }
    }
  }

  return matches;
}

/**
 * Team Americano: Fixed teams, random opponents each round.
 * Avoids rematches (best effort) using previousOpponents map.
 * Unlike Team Mexicano, teams are NOT sorted by points — always random.
 */
export function generateTeamAmericanoRound(
  teams: TeamScore[],
  courtCount: number,
  previousOpponents: Map<string, Set<string>>
): TeamMatch[] {
  const shuffled = shuffle([...teams]);
  const matches: TeamMatch[] = [];
  const used = new Set<string>();

  for (const team of shuffled) {
    if (used.has(team.teamId)) continue;

    // Find best opponent: one we haven't played yet
    let opponent: TeamScore | null = null;
    for (const candidate of shuffled) {
      if (candidate.teamId === team.teamId || used.has(candidate.teamId)) continue;
      const prevOpp = previousOpponents.get(team.teamId);
      if (!prevOpp || !prevOpp.has(candidate.teamId)) {
        opponent = candidate;
        break;
      }
    }
    // Fallback: anyone not used yet
    if (!opponent) {
      opponent = shuffled.find(c => c.teamId !== team.teamId && !used.has(c.teamId)) ?? null;
    }

    if (opponent) {
      matches.push({
        team1Id: team.teamId,
        team2Id: opponent.teamId,
        team1Players: team.players,
        team2Players: opponent.players,
        courtIndex: matches.length % courtCount,
      });
      used.add(team.teamId);
      used.add(opponent.teamId);
    }
  }

  return matches;
}

export function updateTeamOpponentHistory(
  history: Map<string, Set<string>>,
  matches: TeamMatch[]
): Map<string, Set<string>> {
  const updated = new Map(history);
  for (const match of matches) {
    if (!updated.has(match.team1Id)) updated.set(match.team1Id, new Set());
    if (!updated.has(match.team2Id)) updated.set(match.team2Id, new Set());
    updated.get(match.team1Id)!.add(match.team2Id);
    updated.get(match.team2Id)!.add(match.team1Id);
  }
  return updated;
}

// --- Helpers ---

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function pairPlayersAvoidingRepeats(
  players: string[],
  previousPartners: Map<string, Set<string>>
): string[][] {
  const pairs: string[][] = [];
  const used = new Set<string>();

  for (let i = 0; i < players.length; i++) {
    if (used.has(players[i])) continue;

    let bestPartner: string | null = null;

    for (let j = i + 1; j < players.length; j++) {
      if (used.has(players[j])) continue;
      const prevPartners = previousPartners.get(players[i]);
      if (!prevPartners || !prevPartners.has(players[j])) {
        bestPartner = players[j];
        break;
      }
    }

    if (!bestPartner) {
      for (let j = i + 1; j < players.length; j++) {
        if (!used.has(players[j])) {
          bestPartner = players[j];
          break;
        }
      }
    }

    if (bestPartner) {
      pairs.push([players[i], bestPartner]);
      used.add(players[i]);
      used.add(bestPartner);
    }
  }

  return pairs;
}
